// src/layouts/MainLayout.tsx
import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  ListItemButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { HeaderBar } from 'src/components/screen/header';
import { brand } from 'src/themes/palette';
import {
  menu,
  subMenu,
  appBarSx,
  desktopDrawerSx,
  mobileDrawerPaperSx,
  sectionHeaderSx,
  listItemIconSx,
  listItemButtonSx,
  poweredByCaptionSx,
  poweredByLinkSx,
  rootBoxSx,
  mainSx,
  menuButtonSx,
} from './styles';
import { useAppAuth } from 'src/context/use-app-auth';
import { Role } from 'src/api/types/types';
import { useLocationSubscriptionDoc } from 'src/api/billing/hooks/useLocationSubscription';
import {
  DEFAULT_SUBSCRIPTION_TIER,
  fetchNavItems,
  normalizeSubscriptionTier,
} from './nav-items';

export const MainLayout: React.FC = () => {
  const { location: selectedLocation, user } = useAppAuth();
  const { info: locationSubscription } = useLocationSubscriptionDoc(
    selectedLocation?.key,
  );
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSitePressed = () => {
    window.open('https://neberox.com', '_blank');
  };

  const subscriptionTier =
    user?.role === Role.admin
      ? 'tier3'
      : (normalizeSubscriptionTier(
          locationSubscription?.tier ??
            locationSubscription?.planTier ??
            // derive tier from planId if no tier stored
            (locationSubscription?.planId === 'elite'
              ? 'tier3'
              : locationSubscription?.planId === 'growth'
                ? 'tier2'
                : locationSubscription?.planId === 'essential'
                  ? 'tier1'
                  : undefined) ??
            selectedLocation?.subscriptionTier ??
            selectedLocation?.metadata?.subscriptionTier,
        ) ?? DEFAULT_SUBSCRIPTION_TIER);

  const navItems = fetchNavItems(subscriptionTier);

  const handleNavClick = (path: string, enabled: boolean) => {
    if (!enabled) return;
    navigate(path);
    if (isMobile) setOpen(false);
  };

  return (
    <Box sx={rootBoxSx}>
      <CssBaseline />
      <AppBar elevation={2} position="fixed" sx={appBarSx(theme)}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setOpen(v => !v)}
            sx={menuButtonSx}>
            {open && !isMobile ? (
              <ChevronLeftIcon sx={{ color: brand.primary }} />
            ) : (
              <MenuIcon sx={{ color: brand.primary }} />
            )}
          </IconButton>

          <HeaderBar />
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: mobileDrawerPaperSx }}>
          <List sx={menu}>
            {navItems.map(({ section, items }) => (
              <Box key={section} sx={{ mb: 1 }}>
                <Box sx={sectionHeaderSx}>{section}</Box>
                <List sx={subMenu}>
                  {items.map(({ text, icon, path, enabled = true }) => (
                    <ListItemButton
                      disabled={!enabled}
                      selected={routerLocation.pathname === path}
                      key={text}
                      onClick={() => handleNavClick(path, enabled)}>
                      <ListItemIcon sx={listItemIconSx}>{icon}</ListItemIcon>
                      <ListItemText primary={text} sx={{ ml: 2 }} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </List>

          <Typography variant="caption" sx={poweredByCaptionSx}>
            Powered by{' '}
            <Box
              component="span"
              sx={poweredByLinkSx}
              onClick={handleSitePressed}>
              Neberox
            </Box>
          </Typography>
        </Drawer>
      ) : (
        <Drawer variant="permanent" open={open} sx={desktopDrawerSx(open)}>
          <List sx={menu}>
            {navItems.map(({ section, items }) => (
              <Box key={section} sx={{ mb: 1 }}>
                {open && <Box sx={sectionHeaderSx}>{section}</Box>}
                <List sx={subMenu}>
                  {items.map(({ text, icon, path, enabled = true }) => (
                    <ListItemButton
                      disabled={!enabled}
                      selected={routerLocation.pathname === path}
                      key={text}
                      onClick={() => handleNavClick(path, enabled)}
                      sx={listItemButtonSx(open)}>
                      <ListItemIcon sx={listItemIconSx}>{icon}</ListItemIcon>
                      {open && <ListItemText primary={text} sx={{ ml: 2 }} />}
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </List>

          {open && (
            <Typography variant="caption" sx={poweredByCaptionSx}>
              Powered by{' '}
              <Box
                component="span"
                sx={poweredByLinkSx}
                onClick={handleSitePressed}>
                Neberox
              </Box>
            </Typography>
          )}
        </Drawer>
      )}

      <Box component="main" sx={mainSx}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
